import React, {useEffect, useRef, useState} from "react";
import {RequireAuth} from "Components/Security/RequireAuth";
import {Button, Col, Form, Row} from "react-bootstrap";
import {ROUTE_PATHS} from "Config/Router/Routes";
import {Link, useNavigate} from "react-router-dom";
import {AdminCrudList} from "Views/Shared/Crud/AdminCrudList";
import {exportAdminChildren, getChildren} from "Api/Query/ChildrenQuery";
import {deleteChildren} from "Api/Mutation/ChildMutation";
import {SortArrows} from "../Shared/Crud/SortArrows";
import {CourseLevelSelect} from "../../Components/CourseLevel/CourseLevelSelect";
import {LocalStorage} from "../../Services/LocalStorage/LocalStorage";
import {RefreshSpinIcon} from "../../Components/Icons/RefreshSpinIcon";
import {useQuery} from "react-query";
import {forceDownloadFromUrl} from "../../Services/File/FileDownloader";
import {appLoading} from "../../Store/loadingSlice";
import {Notificator} from "../../Services/Notificator/Notificator";
import {useAppDispatch} from "../../hooks";

export const AdminKidsList: React.FC = () => {

    const localStorage = new LocalStorage()
    const PAGE_FILTER = 'admin.kids.page'
    const FORM_FILTER_SEARCH = 'admin.kids.search'
    const FORM_FILTER_COURSE = 'admin.kids.course'
    const navigate = useNavigate()

    // init stored filters
    const defaultSearchFilterValue = localStorage.get(FORM_FILTER_SEARCH) ?? ''
    const defaultCourseFilterValue = localStorage.get(FORM_FILTER_COURSE) ? JSON.parse(localStorage.get(FORM_FILTER_COURSE)) : null
    const page = useRef<number>(localStorage.get(PAGE_FILTER) ? parseInt(localStorage.get(PAGE_FILTER)) : 1)

    const [exporting, setExporting] = useState<boolean>(false)

    const createFilterChangesObject = (
        searchQuery : string = '',
        course: any = null
    ) => {
        const parsedFilters: any = {}

        if (searchQuery) {
            parsedFilters['q'] = searchQuery
        }

        if (course) {
            parsedFilters['courseLevel'] = course.value
        }

        return parsedFilters
    }

    const [sortField, setSortField] = useState<string>('id')
    const [sortDirection, setSortDirection] = useState<string>('-')
    const [refreshSwitch, setRefreshSwitch] = useState<boolean>(false)
    const [customFiltersData, setCustomFiltersData] = useState<{ [key: string]: any }>(
        createFilterChangesObject(
            defaultSearchFilterValue,
            defaultCourseFilterValue
        )
    )
    const [customFiltersDataToApply, setCustomFiltersDataToApply] = useState<{ [key: string]: any }>(
        createFilterChangesObject(
            defaultSearchFilterValue,
            defaultCourseFilterValue
        )
    )
    const [courseLevelFilter, setCourseLevelFilter] = useState<any>(defaultCourseFilterValue)

    const applyFilterChanges = () => {
        const parsedFilters: any = createFilterChangesObject(
            customFiltersData.q ?? '',
            courseLevelFilter ?? null
        )

        setCustomFiltersDataToApply(parsedFilters)
    }

    const sortBy = (field: string, direction: string = '') => {
        const newSort = direction + field
        const currentSort = sortDirection + sortField

        if (newSort === currentSort && sortDirection === direction) {
            direction = direction === '-' ? '' : '-'
        }

        setSortDirection(direction)
        setSortField(field)
    }

    useEffect(() => {
        setRefreshSwitch(!refreshSwitch)
    }, [sortField, sortDirection]);

    useEffect(() => {
        localStorage.set(PAGE_FILTER, page.current)
    }, [page.current]);

    const customHeader: any = () => {
        return (
            <>
                <th className={"align-middle"} key={1}>
                    <Link onClick={() => sortBy('name')} to={"#"}>NOMBRE NIÑO/A</Link>
                    <SortArrows field={'name'} sortField={sortField} sortDirection={sortDirection}/>
                </th>
                <th className={"align-middle"} key={2}>
                    <Link onClick={() => sortBy('number')} to={"#"}>Nº NIÑO</Link>
                    <SortArrows field={'number'} sortField={sortField} sortDirection={sortDirection}/>
                </th>
                <th className={"align-middle"} key={3}>
                    <Link onClick={() => sortBy('nif')} to={"#"}>DNI/NIE</Link>
                    <SortArrows field={'nif'} sortField={sortField} sortDirection={sortDirection}/>
                </th>
                <th className={"align-middle"} key={4}>
                    <Link onClick={() => sortBy('center')} to={"#"}>CENTRO</Link>
                    <SortArrows field={'center'} sortField={sortField} sortDirection={sortDirection}/>
                </th>
                <th className={"align-middle"} key={5}>
                    <Link onClick={() => sortBy('course')} to={"#"}>CURSO</Link>
                    <SortArrows field={'course'} sortField={sortField} sortDirection={sortDirection}/>
                </th>
                <th className={"align-middle"} key={6}>
                    <Link onClick={() => sortBy('tutor')} to={"#"}>TUTOR/A</Link>
                    <SortArrows field={'tutor'} sortField={sortField} sortDirection={sortDirection}/>
                </th>
                <th className={"align-middle"} key={6}>
                    TOTAL INSCRIPCIONES
                </th>
                <th className={"align-middle"} key={7}>
                    ACCIONES
                </th>
            </>
        )
    }

    const dispatch = useAppDispatch()
    const exportQuery = useQuery({
        enabled: false,
        queryKey: ['exportAdminChildren'],
        queryFn: () => exportAdminChildren(customFiltersDataToApply),
        onSuccess: (response: any) => {
            const responseData = response.data
            forceDownloadFromUrl(responseData._result.url);
            setExporting(false)
            dispatch(appLoading({value: false, text: 'Cargando'}))
        },
        onError: () => {
            Notificator.error('Se ha producido un error inesperado.')
            setExporting(false)
            dispatch(appLoading({value: false, text: 'Cargando'}))
        }
    })

    const excelExport = () => {
        setExporting(true)
        exportQuery.refetch()
    }

    const goToTutorDetails = (e: React.MouseEvent, tutorName: string) => {
        e.preventDefault()
        localStorage.set('admin.adminTutorList.search', tutorName)
        localStorage.set('admin.adminTutorList.page', 1)
        navigate(ROUTE_PATHS.ADMIN_TUTOR_LIST)
    }

    return (
        <RequireAuth>

            <AdminCrudList
                defaultPage={page.current}
                onPageChange={(newPage:number) => {
                    page.current = newPage
                }}
                id={"adminKidsList"}
                title={"Listado de Niños/as"}
                customHeader={customHeader()}
                refreshSwitch={refreshSwitch}
                sort={(sortDirection + sortField)}
                queryMethod={getChildren}
                queryName={"adminListChildren"}
                deleteMethod={deleteChildren}
                canSelectMultipleRows={false}
                tableRow={(item: any, key: number) =>
                    <>
                        <td>
                            <Link to={ROUTE_PATHS.ADMIN_KIDS_EDIT.replace(":id", item.id)}>
                                {item.name} {item.surnames}
                            </Link>
                        </td>
                        <td>{item.number}</td>
                        <td>{item.nif}</td>
                        <td>{item.center}</td>
                        <td>{item.courseLevel.name}</td>
                        <td>
                            <p>
                                <Link to={"#"} title={"Ir al detalle del tutor"} onClick={(e) => goToTutorDetails(e, item.tutor.name)}>{item.tutor.name}</Link>
                            </p>
                            <p>Telf: {item.tutor.phoneNumbers}</p>
                        </td>
                        <td>{item.totalInscriptions}</td>
                        <td className={"text-center align-middle"}>
                            <Button
                                className={"purple-btn btn-sm"}
                                href={ROUTE_PATHS.ADMIN_KIDS_EDIT.replace(":id", item.id)}
                            >
                                Modificar
                            </Button>
                        </td>
                    </>
                }
                customFiltersData={customFiltersDataToApply}
                customFilters={
                    <>
                        <Col xs={12} sm={6}>
                            <Form.Group controlId={"queryFilter"}>
                                <Form.Label>
                                    Buscar
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    onChange={(e: any) => {
                                        const value = e.target.value ?? ''
                                        setCustomFiltersData({...customFiltersData, q: value})
                                        localStorage.set(FORM_FILTER_SEARCH, e.target.value ?? '')
                                    }}
                                    value={customFiltersData.q ?? ''}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={6} md={3}>
                            <Form.Group controlId={"queryFilter"}>
                                <Form.Label>Curso</Form.Label>
                                <CourseLevelSelect
                                    onChange={(option: any) => {
                                        setCourseLevelFilter(option ?? null)
                                        localStorage.set(FORM_FILTER_COURSE, option ? JSON.stringify(option) : null)
                                    }}
                                    value={courseLevelFilter}
                                />
                            </Form.Group>
                        </Col>
                        <Row className={"mt-2"}>
                            <Col>
                                <Button
                                    className={"purple-btn"}
                                    onClick={applyFilterChanges}
                                >Buscar</Button>
                                &nbsp;
                                <Button
                                    variant={"secondary"}
                                    onClick={() => {
                                        setCourseLevelFilter(null)
                                        setCustomFiltersData({})
                                        setCustomFiltersDataToApply({})
                                        localStorage.set(FORM_FILTER_SEARCH, '')
                                        localStorage.set(FORM_FILTER_COURSE, null)
                                        page.current = 1
                                        localStorage.set(PAGE_FILTER, page.current)
                                    }
                                    }
                                >Limpiar filtros</Button>
                            </Col>
                        </Row>
                    </>
                }
                 export={
                    <Button
                        className={"purple-btn mx-4"}
                        onClick={excelExport}
                        disabled={exporting}>
                        {exporting && <><RefreshSpinIcon/>&nbsp;&nbsp;</>}
                        <>Exportar</>
                    </Button>
                }
            />

        </RequireAuth>
    )
}
